/* The Dashboard's Statistics requests, one per source × range × Account however
   many widgets ask for it. A reload keeps showing what was loaded until the new
   answer arrives; a failure is kept per request so only its widgets say so.
   A request lives only while a widget shows its range: one nobody shows any more
   (yesterday's after midnight, a period switched away from) is dropped, so
   Refresh never re-reads it — a tab left open for days kept re-reading every
   range it had ever shown. */

import { useCallback, useMemo, useRef, useState } from 'react';
import { api } from '../api/client';
import type { Range, Source, SourceCache, SourceEntry } from './context';

interface Slot extends SourceEntry {
  source: Source;
  range: Range;
  accountId?: string;
  seq: number;
  /** Widgets showing this range right now. */
  users: number;
}

const NO_ACCESS = /access|permission|forbidden/i;

export function useSourceCache(accountId: string | undefined): SourceCache & { reloadAll: () => void } {
  const slots = useRef(new Map<string, Slot>());
  const [version, setVersion] = useState(0);
  const bump = useCallback(() => setVersion((v) => v + 1), []);

  const fetchSlot = useCallback((key: string) => {
    const slot = slots.current.get(key);
    if (!slot) return;
    const seq = slot.seq + 1;
    slots.current.set(key, { ...slot, seq, loading: true });
    bump();
    const params = { start: slot.range.start, end: slot.range.end, accountId: slot.accountId };
    const request = slot.source === 'stats' ? api.getStatistics(params) : api.getChatStats(params);
    request.then(
      (data) => {
        const cur = slots.current.get(key);
        if (!cur || cur.seq !== seq) return;
        slots.current.set(key, { ...cur, data, error: null, noAccess: false, loading: false });
        bump();
      },
      (err: unknown) => {
        const cur = slots.current.get(key);
        if (!cur || cur.seq !== seq) return;
        const message = (err as Error)?.message || 'failed';
        // A failed background re-read keeps the figures already shown.
        if (cur.data) slots.current.set(key, { ...cur, loading: false });
        else slots.current.set(key, { ...cur, data: null, error: message, noAccess: NO_ACCESS.test(message), loading: false });
        bump();
      },
    );
  }, [bump]);

  const ensure = useCallback((key: string, source: Source, range: Range) => {
    const slot = slots.current.get(key);
    if (slot) {
      slots.current.set(key, { ...slot, users: slot.users + 1 });
      return;
    }
    slots.current.set(key, { source, range, accountId, data: null, error: null, noAccess: false, loading: true, seq: 0, users: 1 });
    fetchSlot(key);
  }, [accountId, fetchSlot]);

  const release = useCallback((key: string) => {
    const slot = slots.current.get(key);
    if (!slot) return;
    if (slot.users > 1) slots.current.set(key, { ...slot, users: slot.users - 1 });
    else slots.current.delete(key);
  }, []);

  const get = useCallback((key: string) => slots.current.get(key), []);

  /** Re-read what the widgets show for the current Account, quietly. */
  const reloadAll = useCallback(() => {
    for (const [key, slot] of slots.current) {
      if (slot.accountId === accountId && slot.users > 0) fetchSlot(key);
      else slots.current.delete(key);
    }
  }, [accountId, fetchSlot]);

  return useMemo(
    () => ({ get, ensure, release, reload: fetchSlot, reloadAll, version }),
    [get, ensure, release, fetchSlot, reloadAll, version],
  );
}
